import DocumentUploadNew from '@/app/common/components/Documents/DocumentUploadNew'
import FormErrorMessage from '@/app/common/components/FormErrorMessage'
import Image from '@/app/common/components/Image/Image'
import MultiQueryPageLoader from '@/app/common/components/PageLoader/MultiQueryPageLoader'
import { Select } from '@/app/common/components/Select'
import { CurrencyInputField } from '@/app/common/components/currency-input/currency-input'
import { useTemporaryFileUpload } from '@/app/common/hooks/useTemporaryFileUpload'
import { currencyFormatter } from '@/app/common/utilities'
import { TrashCanIcon } from '@/app/common/icons/general'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button'
import { useAppAlert } from '@/app/AppAlertProvider'
import { Input } from '@/common/components/Input'
import { Label } from '@/shad/ui/label'
import { selectFamilyApplication } from '@/storage/applications/familyApplicationSlice'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import { InvoiceFile } from '../create_provider_request_form'
import { useStudentPurses } from '../api/usePaymentProviders'
import { DOCUMENT_TYPES } from '@/apis/providers/types/provider.types'
import { Button } from '@/common/components/Button'

const InvoiceItemSchema = z.object({
  file_name: z.string(),
  student_id: z.number().int().min(1, 'select_student'),
  invoice_number: z.string().min(1, 'invoice_number_required'),
  invoice_date: z.string().min(1, 'invoice_date_required'),
  amount: z.number().min(0.01, 'amount_must_be_greater_than_0'),
  notes: z.string().optional(),
})

const UploadInvoicesSchema = z.object({
  invoices: z.array(InvoiceItemSchema).min(1),
})

type UploadInvoicesForm = z.infer<typeof UploadInvoicesSchema>

type Props = {
  onNext: (invoices: InvoiceFile[], total: number) => void
  defaultValues: InvoiceFile[]
}

export function UploadProviderInvoices({ onNext, defaultValues }: Props) {
  const { t } = useTranslation()
  const { fireAlert } = useAppAlert()
  const programId = useSelector(selectProgramID)
  const application = useSelector(selectFamilyApplication)
  const [phase, setPhase] = useState<'upload' | 'metadata'>(() =>
    defaultValues.length > 0 ? 'metadata' : 'upload'
  )

  const studentPursesQuery = useStudentPurses({
    program_id: programId,
    application_id: application.application_id,
  })

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<UploadInvoicesForm>({
    resolver: zodResolver(UploadInvoicesSchema),
    defaultValues: {
      invoices: defaultValues.map((f) => ({
        file_name: f.file_name,
        student_id: f.student_id,
        invoice_number: f.invoice_number,
        invoice_date: f.invoice_date,
        amount: f.amount,
        notes: f.notes ?? '',
      })),
    },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'invoices' })
  const invoices = watch('invoices')
  const invoiceTotal = invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0)

  const uploadMutation = useTemporaryFileUpload({
    onError: (error) => {
      fireAlert({
        title: true,
        titleText: t('document_upload_error'),
        message: error.message,
        variant: 'error',
      })
    },
    onSuccess: (data) => {
      data.forEach((item) => {
        append({
          file_name: item.file_name,
          student_id: 0,
          invoice_number: '',
          invoice_date: '',
          amount: 0,
          notes: '',
        })
      })
      setPhase('metadata')
    },
  })

  const onFormSubmit = (data: UploadInvoicesForm) => {
    const invoiceFiles: InvoiceFile[] = data.invoices.map((inv) => ({
      file_name: inv.file_name,
      document_type: DOCUMENT_TYPES.INVOICE,
      student_id: inv.student_id,
      invoice_number: inv.invoice_number,
      invoice_date: inv.invoice_date,
      amount: inv.amount,
      ...(inv.notes ? { notes: inv.notes } : {}),
    }))
    onNext(invoiceFiles, invoiceTotal)
  }

  if (phase === 'upload') {
    return (
      <FamilyWizardLayout>
        <FamilyWizardLayout.Header>
          <h1>{t('upload_documents')}</h1>
        </FamilyWizardLayout.Header>
        <FamilyWizardLayout.Content className="items-center w-full justify-center">
          {uploadMutation.isPending ? (
            <p className="text-gray-500 text-sm mt-4">{t('uploading')}…</p>
          ) : (
            <DocumentUploadNew
              allowMultiple={true}
              isPdfAllowed={true}
              onUpload={(files) => uploadMutation.mutate(files)}
            />
          )}
        </FamilyWizardLayout.Content>
      </FamilyWizardLayout>
    )
  }

  return (
    <MultiQueryPageLoader queries={[studentPursesQuery] as const}>
      {([apiPurses]) => {
        const studentMap = new Map<number, string>()
        apiPurses.forEach((p) => {
          if (!studentMap.has(p.student_id))
            studentMap.set(p.student_id, p.student_name)
        })
        const students = Array.from(studentMap.entries()).map(([id, name]) => ({
          id,
          name,
        }))

        return (
          <FamilyWizardLayout>
            <FamilyWizardLayout.Header>
              <h1>{t('invoice_details')}</h1>
            </FamilyWizardLayout.Header>
            <FamilyWizardLayout.Content className="w-full">
              <form
                id="invoice-metadata-form"
                onSubmit={handleSubmit(onFormSubmit)}
              >
                <div className="space-y-4">
                  {fields.map((field, index) => (
                    <div
                      key={field.id}
                      className="border border-gray-200 rounded-lg p-4 space-y-3"
                    >
                      {/* Card header: thumbnail + filename + remove */}
                      <div className="flex items-start gap-3">
                        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                          <Image
                            path={field.file_name}
                            className="object-cover w-full h-full"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                        </div>
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                          aria-label={t('remove')}
                        >
                          <TrashCanIcon className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Student */}
                      <div className="space-y-1">
                        <Label htmlFor={`student-${index}`}>
                          {t('student')} *
                        </Label>
                        <Controller
                          name={`invoices.${index}.student_id`}
                          control={control}
                          render={({ field: f }) => (
                            <Select
                              id={`student-${index}`}
                              value={f.value || ''}
                              onChange={(e) =>
                                f.onChange(
                                  Number(
                                    (e.target as HTMLSelectElement).value
                                  )
                                )
                              }
                            >
                              <option value="">{t('select_student')}</option>
                              {students.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </Select>
                          )}
                        />
                        {errors.invoices?.[index]?.student_id && (
                          <FormErrorMessage
                            message={
                              errors.invoices[index].student_id?.message ?? ''
                            }
                          />
                        )}
                      </div>

                      {/* Invoice number */}
                      <div className="space-y-1">
                        <Label htmlFor={`inv-num-${index}`}>
                          {t('invoice_number')} *
                        </Label>
                        <Controller
                          name={`invoices.${index}.invoice_number`}
                          control={control}
                          render={({ field: f }) => (
                            <Input
                              id={`inv-num-${index}`}
                              type="text"
                              {...f}
                            />
                          )}
                        />
                        {errors.invoices?.[index]?.invoice_number && (
                          <FormErrorMessage
                            message={
                              errors.invoices[index].invoice_number?.message ??
                              ''
                            }
                          />
                        )}
                      </div>

                      {/* Invoice date */}
                      <div className="space-y-1">
                        <Label>{t('payment_details.invoice_date')} *</Label>
                        <Controller
                          name={`invoices.${index}.invoice_date`}
                          control={control}
                          render={({ field: f }) => (
                            <input
                              id={`inv-date-${index}`}
                              type="date"
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                              {...f}
                            />
                          )}
                        />
                        {errors.invoices?.[index]?.invoice_date && (
                          <FormErrorMessage
                            message={
                              errors.invoices[index].invoice_date?.message ?? ''
                            }
                          />
                        )}
                      </div>

                      {/* Amount */}
                      <Controller
                        name={`invoices.${index}.amount`}
                        control={control}
                        render={({ field: f }) => (
                          <CurrencyInputField
                            id={`inv-amount-${index}`}
                            label="payment_details.invoice_amount"
                            required
                            value={f.value || ''}
                            onChange={(val) => f.onChange(val)}
                            error={
                              errors.invoices?.[index]?.amount?.message
                            }
                          />
                        )}
                      />

                      {/* Notes */}
                      <div className="space-y-1">
                        <Label htmlFor={`inv-notes-${index}`}>
                          {t('payment_details.notes')}
                        </Label>
                        <Controller
                          name={`invoices.${index}.notes`}
                          control={control}
                          render={({ field: f }) => (
                            <textarea
                              id={`inv-notes-${index}`}
                              rows={2}
                              placeholder={t('payment_details.optional_notes')}
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6"
                              {...f}
                            />
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Running total */}
                <div className="mt-4 p-3 bg-gray-50 rounded-md flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">
                    {t('payment_details.payment_amount')}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {currencyFormatter.format(invoiceTotal)}
                  </span>
                </div>
              </form>

              <div className="flex gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setPhase('upload')}
                  className="px-10"
                >
                  {t('upload_documents')}
                </Button>
                <SubmitButton
                  forceDisabled={invoiceTotal <= 0 || uploadMutation.isPending}
                  form="invoice-metadata-form"
                  status="idle"
                  buttonTxt={t('next')}
                  className="px-10"
                />
              </div>
            </FamilyWizardLayout.Content>
          </FamilyWizardLayout>
        )
      }}
    </MultiQueryPageLoader>
  )
}
